/**
 * Хуки аутентификации поверх OAuth2 (bcf): self-профиль + редиректы.
 *
 * Логина/логаута как HTTP-вызовов больше нет: вход и выход — браузерные
 * редиректы на `/_/api/oauth2/v1/login|logout` (см. `../clients/colver.ts`).
 * `useMe` тянет colver-профиль (`GET /users/self`) по session-cookie.
 */
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {colverAuthClient} from '../clients/colver';
import {clearStoredUser, readStoredUser, writeStoredUser} from '../lib/storage';
import type {CurrentUser, TourKey} from '../types';

const keys = {
  me: () => ['auth', 'colver', 'self'] as const,
};

/**
 * Colver-профиль текущего пользователя.
 *
 * Запрос выполняется всегда (cookie-сессия может существовать без localStorage);
 * успешный ответ зеркалится в localStorage — sid оттуда использует dev-режим
 * (X-Tests-User-Sid) и axios-интерсепторы остальных модулей.
 */
const useMe = (params?: {enabled?: boolean}) =>
  useQuery<CurrentUser>({
    queryKey: keys.me(),
    queryFn: async () => {
      const user = await colverAuthClient.self();
      writeStoredUser(user);
      return user;
    },
    // Тёплый старт из localStorage, чтобы не моргало loading-состояние.
    initialData: () => readStoredUser() ?? undefined,
    enabled: params?.enabled ?? true,
    retry: 0,
    staleTime: 5 * 60_000,
  });

/**
 * Отметить ознакомительный тур показанным. Профиль в кэше и localStorage
 * обновляем сразу: тур не должен открыться снова, пока идёт запрос.
 */
const useMarkTourSeen = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tourKey: TourKey) => colverAuthClient.markTourSeen(tourKey),
    onMutate: (tourKey) => {
      const user = qc.getQueryData<CurrentUser>(keys.me());
      if (!user || user.seenTours?.includes(tourKey)) return;
      const next = {...user, seenTours: [...(user.seenTours ?? []), tourKey]};
      qc.setQueryData(keys.me(), next);
      writeStoredUser(next);
    },
  });
};

/** Начать OAuth2-вход: hard redirect на bcf login (с возвратом на redirectPage). */
const startLogin = (redirectPage?: string): void => {
  window.location.assign(colverAuthClient.oauth2LoginUrl(redirectPage));
};

/** Выйти: почистить локальное состояние и уйти на bcf logout (hard redirect). */
const startLogout = async (): Promise<void> => {
  clearStoredUser();
  try {
    await fetch(colverAuthClient.oauth2LogoutUrl(), {
      credentials: 'include',
      redirect: 'manual',
    });
  } catch (e) {
    console.warn('logout request failed (session cleared anyway)', e);
  }
  window.location.assign(import.meta.env.BASE_URL);
};

export const colverAuthHooker = {
  useMe,
  useMarkTourSeen,
  startLogin,
  startLogout,
  keys,
};
